import styles from "./StatusTag.module.css";
import type { TaskStatus, Priority, Severity, ProjectStatus, EpicStatus, SprintStatus, SessionUser } from "../../types/interfaces";

type Tone = "neutral" | "brand" | "info" | "warn" | "ok" | "danger";

const TONE: Record<Tone, { fg: string; bg: string }> = {
  neutral: { fg: "var(--osi-text-muted)", bg: "var(--osi-sunken)" },
  brand: { fg: "var(--osi-brand-700)", bg: "var(--osi-brand-100)" },
  info: { fg: "var(--osi-brand-600)", bg: "var(--osi-brand-100)" },
  warn: { fg: "var(--osi-warn)", bg: "var(--osi-warn-soft)" },
  ok: { fg: "var(--osi-ok)", bg: "var(--osi-ok-soft)" },
  danger: { fg: "var(--osi-danger)", bg: "var(--osi-danger-soft)" },
};

export const TASK_STATUS: Record<TaskStatus, { label: string; tone: Tone }> = {
  backlog: { label: "Backlog", tone: "neutral" },
  ready: { label: "Ready", tone: "info" },
  in_progress: { label: "In progress", tone: "brand" },
  in_review: { label: "In review", tone: "warn" },
  done: { label: "Done", tone: "ok" },
};

export const PRIORITY: Record<Priority, { label: string; tone: Tone }> = {
  low: { label: "Low", tone: "neutral" },
  medium: { label: "Medium", tone: "neutral" },
  high: { label: "High", tone: "warn" },
  urgent: { label: "Urgent", tone: "danger" },
};
export const TASK_PRIORITY = PRIORITY;

export const SEVERITY: Record<Severity, { label: string; tone: Tone }> = {
  critical: { label: "Critical", tone: "danger" },
  major: { label: "Major", tone: "warn" },
  minor: { label: "Minor", tone: "info" },
  trivial: { label: "Trivial", tone: "neutral" },
};

export const PROJECT_STATUS: Record<ProjectStatus, { label: string; tone: Tone }> = {
  active: { label: "Active", tone: "ok" },
  on_hold: { label: "On hold", tone: "warn" },
  completed: { label: "Completed", tone: "info" },
  archived: { label: "Archived", tone: "neutral" },
};

export const EPIC_STATUS: Record<EpicStatus, { label: string; tone: Tone }> = {
  open: { label: "Open", tone: "neutral" },
  in_progress: { label: "In progress", tone: "brand" },
  done: { label: "Done", tone: "ok" },
};

export const SPRINT_STATUS: Record<SprintStatus, { label: string; tone: Tone }> = {
  planned: { label: "Planned", tone: "neutral" },
  active: { label: "Active", tone: "brand" },
  completed: { label: "Completed", tone: "ok" },
};

export const ROLE: Record<SessionUser["role"], { label: string; tone: Tone }> = {
  manager: { label: "Manager", tone: "brand" },
  agent: { label: "Agent", tone: "info" },
  requester: { label: "Requester", tone: "neutral" },
};

interface Props { label: string; tone?: Tone; dot?: boolean; }

export const StatusTag = ({ label, tone = "neutral", dot }: Props) => {
  const t = TONE[tone];
  return (
    <span className={styles.tag} style={{ color: t.fg, background: t.bg }}>
      {dot && <span className={styles.dot} />}
      {label}
    </span>
  );
};

export const TaskStatusTag = ({ status }: { status: TaskStatus }) => (
  <StatusTag {...TASK_STATUS[status]} dot />
);
export const PriorityTag = ({ priority }: { priority: Priority }) => (
  <StatusTag label={`Priority · ${PRIORITY[priority].label}`} tone={PRIORITY[priority].tone} />
);
export const SeverityTag = ({ severity }: { severity: Severity }) => (
  <StatusTag label={`Severity · ${SEVERITY[severity].label}`} tone={SEVERITY[severity].tone} dot />
);
// compact variants (label only) for dense lists and board cards
export const PriorityChip = ({ priority }: { priority: Priority }) => <StatusTag {...PRIORITY[priority]} />;
export const SeverityChip = ({ severity }: { severity: Severity }) => <StatusTag {...SEVERITY[severity]} dot />;
export const TaskPriorityTag = PriorityChip;
export const ProjectStatusTag = ({ status }: { status: ProjectStatus }) => (
  <StatusTag {...PROJECT_STATUS[status]} dot />
);

export const EpicStatusTag = ({ status }: { status: EpicStatus }) => <StatusTag {...EPIC_STATUS[status]} dot />;
export const SprintStatusTag = ({ status }: { status: SprintStatus }) => <StatusTag {...SPRINT_STATUS[status]} dot />;
export const RoleTag = ({ role }: { role: SessionUser["role"] }) => <StatusTag {...ROLE[role]} />;
